// QBR metrics — pure helpers that map a "raw inputs" shape per metric
// into the value shape that grading.js expects, plus a tiny schema that
// drives the Data step UI (which input fields to show per metric).
//
// Each metric has:
//   - inputs[]:  { key, label, unit, placeholder }   — fields the FMXer enters / FMX pre-fills
//   - compute(raw): returns the value object grading.js consumes
//
// Keeping this pure (no React, no FMX, no DB) means the Data step can
// drive both manual entry and (later) the FMX auto-pull through the
// same code path.

export const METRIC_SCHEMA = {
  completion_rate: {
    label: 'Maintenance Activity',
    inputs: [
      { key: 'completed', label: 'Completed WOs',  unit: 'count' },
      { key: 'total',     label: 'Total WOs',      unit: 'count' },
    ],
    compute: ({ completed, total }) => {
      const c = num(completed);
      const t = num(total);
      if (c == null && t == null) return null;
      if (!t) return null;
      return { pct: round1(((c || 0) / t) * 100), completed: c ?? 0, total: t ?? 0 };
    },
    formatValue: v => v ? `${v.pct}%` : '—',
  },

  response_time: {
    label: 'Team Performance',
    // Three timing series, each with median + mean editable inputs:
    //   response   — first comment on the request (or resolution if no
    //                comment was left). Graded.
    //   assignment — first time the request was assigned. Auxiliary,
    //                shown so the team can see pick-up speed separately
    //                from response speed.
    //   resolution — when the request was actually resolved. Graded.
    // Mode flows through the fetch result for the future line-graph slice
    // and isn't an editable input.
    inputs: [
      { key: 'responseDays',       label: 'Median first response',     unit: 'days' },
      { key: 'responseMeanDays',   label: 'Average first response',    unit: 'days' },
      { key: 'assignmentDays',     label: 'Median first assignment',   unit: 'days' },
      { key: 'assignmentMeanDays', label: 'Average first assignment',  unit: 'days' },
      { key: 'resolutionDays',     label: 'Median resolution',         unit: 'days' },
      { key: 'resolutionMeanDays', label: 'Average resolution',        unit: 'days' },
      { key: 'trend',              label: 'Trend vs last quarter',     unit: 'select',
        options: [
          { value: 'down', label: 'Improving' },
          { value: 'flat', label: 'Flat' },
          { value: 'up',   label: 'Worsening' },
        ] },
    ],
    compute: ({
      responseDays, responseMeanDays,
      assignmentDays, assignmentMeanDays,
      resolutionDays, resolutionMeanDays,
      trend,
    }) => {
      const respMed = num(responseDays);
      const respAvg = num(responseMeanDays);
      const asnMed  = num(assignmentDays);
      const asnAvg  = num(assignmentMeanDays);
      const resMed  = num(resolutionDays);
      const resAvg  = num(resolutionMeanDays);
      if ([respMed, respAvg, asnMed, asnAvg, resMed, resAvg].every(v => v == null)) return null;
      // Missing series stay null. They used to default to 0 days — the
      // best possible value — which let a metric with (say) no
      // resolutions grade as if resolution were instant. grading.js
      // declines to grade when either graded median is null.
      return {
        responseDays:       respMed,
        responseMeanDays:   respAvg,
        assignmentDays:     asnMed,
        assignmentMeanDays: asnAvg,
        resolutionDays:     resMed,
        resolutionMeanDays: resAvg,
        trend: trend || 'flat',
      };
    },
    formatValue: v => {
      if (!v) return '—';
      const fmtPair = (med, avg) => {
        const bits = [];
        if (med != null) bits.push(avg != null ? `${round1(med)}d med` : `${round1(med)}d`);
        if (avg != null) bits.push(`${round1(avg)}d avg`);
        return bits.join(' · ');
      };
      const parts = [];
      if (v.responseDays != null || v.responseMeanDays != null) {
        parts.push(`response ${fmtPair(v.responseDays, v.responseMeanDays)}`);
      }
      if (v.assignmentDays != null || v.assignmentMeanDays != null) {
        parts.push(`assign ${fmtPair(v.assignmentDays, v.assignmentMeanDays)}`);
      }
      if (v.resolutionDays != null || v.resolutionMeanDays != null) {
        parts.push(`resolve ${fmtPair(v.resolutionDays, v.resolutionMeanDays)}`);
      }
      return parts.length ? parts.join(' · ') : '—';
    },
  },

  planned_vs_reactive: {
    label: 'Planned vs Reactive',
    inputs: [
      { key: 'planned',  label: 'Planned WOs (PMs)',   unit: 'count' },
      { key: 'reactive', label: 'Reactive WOs',         unit: 'count' },
    ],
    compute: ({ planned, reactive }) => {
      const p = num(planned);
      const r = num(reactive);
      if (p == null && r == null) return null;
      const total = (p || 0) + (r || 0);
      if (total === 0) return null;
      return { pct: round1(((p || 0) / total) * 100) };
    },
    formatValue: v => v ? `${v.pct}% planned` : '—',
  },

  pm_compliance: {
    label: 'PM Compliance',
    inputs: [
      { key: 'onTimePct',  label: 'On-time completion',   unit: '%' },
      { key: 'anyTimePct', label: 'Any-time completion',  unit: '%' },
      { key: 'targetPct',  label: 'Committed target',     unit: '%' },
    ],
    // Grade is computed against on-time vs target (the harder, standard
    // CMMS metric). any-time is carried through for display only.
    // targetPct/actualPct stay null when unknown — a missing target
    // used to default to 0, which made delta = actual − 0 and handed
    // out an automatic A. grading.js declines to grade instead.
    compute: ({ onTimePct, anyTimePct, targetPct }) => {
      const o = num(onTimePct);
      const a = num(anyTimePct);
      const t = num(targetPct);
      if (o == null && a == null && t == null) return null;
      return {
        onTimePct:  o,
        anyTimePct: a,
        targetPct:  t,
        actualPct:  o ?? a,
      };
    },
    formatValue: v => {
      if (!v) return '—';
      const parts = [];
      if (v.onTimePct  != null) parts.push(`${round1(v.onTimePct)}% on-time`);
      if (v.anyTimePct != null) parts.push(`${round1(v.anyTimePct)}% any-time`);
      if (v.targetPct  != null) parts.push(`target ${round1(v.targetPct)}%`);
      return parts.join(' · ');
    },
  },

  backlog_aging: {
    label: 'Open Backlog & Aging',
    inputs: [
      { key: 'overdue30dPct',   label: '% overdue >30 days',    unit: '%' },
      { key: 'oldestDays',      label: 'Age of oldest open WO', unit: 'days' },
      { key: 'openCount',       label: 'Open WO count',         unit: 'count' },
      { key: 'overdue30dCount', label: 'Overdue >30d count',    unit: 'count' },
    ],
    compute: ({ overdue30dPct, oldestDays, openCount, overdue30dCount }) => {
      const o = num(overdue30dPct);
      const d = num(oldestDays);
      const op = num(openCount);
      const oc = num(overdue30dCount);
      if (o == null && d == null && op == null && oc == null) return null;
      return {
        overdue30dPct:   o ?? 0,
        oldestDays:      d ?? 0,
        openCount:       op ?? 0,
        overdue30dCount: oc ?? 0,
      };
    },
    formatValue: v => v ? `${round1(v.overdue30dPct)}% overdue · oldest ${v.oldestDays}d` : '—',
  },

  adoption: {
    label: 'Adoption & Engagement',
    inputs: [
      { key: 'active30Pct', label: 'Active in last 30 days', unit: '%' },
      { key: 'active90Pct', label: 'Active in last 90 days', unit: '%' },
      { key: 'mobilePct',   label: 'Mobile usage',           unit: '%' },
    ],
    // Grade is computed against the 30-day rate (the stricter measure)
    // vs the standard thresholds. 90-day is shown for context — a
    // strong 30/90 spread tells the FMXer whether the customer is sliding
    // off or just had a quiet month.
    compute: ({ active30Pct, active90Pct, mobilePct }) => {
      const a30 = num(active30Pct);
      const a90 = num(active90Pct);
      const m   = num(mobilePct);
      if (a30 == null && a90 == null && m == null) return null;
      return {
        active30Pct: a30,
        active90Pct: a90,
        // Null when not entered — FMX's API has no mobile-usage signal,
        // so the auto-pull can never fill this. It used to default to 0,
        // which failed every tier's mobile_min_pct and forced a D.
        // grading.js only applies the mobile gate when the value exists.
        mobilePct:   m,
        // grading.js reads `activePct` for this metric — feed it the 30d
        // value (the harder bar). When 30d is missing, fall back to 90d
        // so partial data still grades; null when neither is known.
        activePct: a30 ?? a90,
      };
    },
    formatValue: v => {
      if (!v) return '—';
      const parts = [];
      if (v.active30Pct != null) parts.push(`${round1(v.active30Pct)}% in 30d`);
      if (v.active90Pct != null) parts.push(`${round1(v.active90Pct)}% in 90d`);
      if (v.mobilePct   != null) parts.push(`${round1(v.mobilePct)}% mobile`);
      return parts.join(' · ');
    },
  },

  // Same shape + grading rule as `adoption`, but the underlying user
  // population is filtered to the user-types the FMXer marked as
  // 'power users' on the Setup step. Mobile% mirrors the overall
  // value (FMX's API doesn't surface a per-user mobile signal so we
  // can't filter it further).
  adoption_power: {
    label: 'Power-User Adoption & Engagement',
    inputs: [
      { key: 'active30Pct', label: 'Active in last 30 days', unit: '%' },
      { key: 'active90Pct', label: 'Active in last 90 days', unit: '%' },
      { key: 'mobilePct',   label: 'Mobile usage',           unit: '%' },
    ],
    compute: ({ active30Pct, active90Pct, mobilePct }) => {
      const a30 = num(active30Pct);
      const a90 = num(active90Pct);
      const m   = num(mobilePct);
      if (a30 == null && a90 == null && m == null) return null;
      return {
        active30Pct: a30,
        active90Pct: a90,
        // Same null semantics as `adoption` above.
        mobilePct:   m,
        activePct:   a30 ?? a90,
      };
    },
    formatValue: v => {
      if (!v) return '—';
      const parts = [];
      if (v.active30Pct != null) parts.push(`${round1(v.active30Pct)}% in 30d`);
      if (v.active90Pct != null) parts.push(`${round1(v.active90Pct)}% in 90d`);
      if (v.mobilePct   != null) parts.push(`${round1(v.mobilePct)}% mobile`);
      return parts.join(' · ');
    },
  },
};

// Treat empty string / null / undefined / non-numeric as "not entered".
// Returning null from this helper lets each compute() short-circuit when
// the FMXer hasn't filled the metric in yet.
function num(raw) {
  if (raw === '' || raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function round1(n) {
  return Math.round(Number(n) * 10) / 10;
}

// Empty input shape for a given metric — useful when the FMXer is filling
// values manually before an FMX pull happens.
export function emptyRawInputs() {
  const out = {};
  for (const [metric, def] of Object.entries(METRIC_SCHEMA)) {
    out[metric] = {};
    for (const inp of def.inputs) out[metric][inp.key] = '';
  }
  return out;
}
